import React from "react";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import { BackButton } from "../../../../Components/Utils/Buttons";
import { useToast } from "../../../../Components/Others/ToastServices";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../Services/axiosCommon";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const FeatureSettings = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Fetch feature toggles using React Query
  const { data: features = [], isLoading: loading } = useQuery({
    queryKey: ["feature_toggles"],
    queryFn: async () => {
      const res = await axiosInstance.get("/feature_toggles/");
      return Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
          ? res.data
          : [];
    },
    staleTime: 60000,
  });

  const getFeatureTitle = (feature) => {
    if (!feature?.feature_name) return "";
    switch (feature.feature_name) {
      case "email_parsing":
        return t("Email Parsing");
      case "auto_quote_creation":
        return t("Auto Quote Creation");
      default:
        return feature.feature_name
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
    }
  };

  // Update feature toggle mutation
  const toggleMutation = useMutation({
    mutationFn: async ({ feature, newStatus }) => {
      const res = await axiosInstance.put(`/feature_toggles/${feature.id}`, {
        is_enabled: newStatus,
      });
      return { res: res.data, feature, newStatus };
    },
    onSuccess: ({ res, feature, newStatus }) => {
      if (res?.status !== false) {
        const featureTitle = getFeatureTitle(feature);
        toast.open({
          type: "success",
          message: t("Feature Status Updated"),
          description: `${featureTitle} ${newStatus ? t("Enabled") : t("Disabled")}.`,
        });
        queryClient.invalidateQueries({ queryKey: ["feature_toggles"] });
      } else {
        toast.open({
          type: "error",
          message: t("Error"),
          description: res?.error || t("Failed to update feature status."),
        });
      }
    },
    onError: (err) => {
      console.error("Failed to update feature toggle:", err);
      toast.open({
        type: "error",
        message: t("Error"),
        description:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          t("Failed to update feature status."),
      });
    },
  });

  const handleToggle = (feature) => {
    toggleMutation.mutate({ feature, newStatus: !feature.is_enabled });
  };

  const visibleFeatures = features.filter(
    (feature) => feature?.feature_name !== "email_parsing"
  );

  return (
    <div className="w-full h-full px-4 py-2">
      {/* Top Navigation */}
      <div className="w-full flex justify-between items-center">
        <Breadcrumb
          items={[
            { name: t("Settings"), link: "/settings" },
            { name: t("Feature Settings") },
          ]}
        />
        <BackButton />
      </div>

      {/* Main Card Container */}
      <div className="w-full h-[calc(100vh-145px)] bg-white mt-3 mx-auto rounded-md overflow-y-auto p-4 border border-gray-100 shadow-sm">
        <div className="mb-4 pb-3 border-b border-gray-200">
          <h2 className="text-base font-bold text-gray-800">{t("Feature Settings")}</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {t("Enable or disable specific features across the platform.")}
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
            <p className="mt-3 text-xs text-gray-500">{t("Loading feature settings...")}</p>
          </div>
        ) : visibleFeatures.length === 0 ? (
          <div className="text-center py-12 text-gray-500 text-xs">
            {t("No feature settings available.")}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {visibleFeatures.map((feature) => {
              const isUpdating =
                toggleMutation.isPending &&
                toggleMutation.variables?.feature?.id === feature.id;
              const isEnabled = Boolean(feature.is_enabled);

              return (
                <div
                  key={feature.id}
                  className="py-4 flex items-center justify-between gap-4"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-800">
                        {getFeatureTitle(feature)}
                      </h3>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${isEnabled
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-gray-100 text-gray-600"
                          }`}
                      >
                        {isEnabled ? t("Enabled") : t("Disabled")}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 max-w-2xl">
                      {feature.description ? t(feature.description) : ""}
                    </p>
                  </div>

                  {/* Simple Toggle Switch */}
                  <button
                    type="button"
                    disabled={isUpdating}
                    onClick={() => handleToggle(feature)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isUpdating ? "opacity-60 cursor-not-allowed" : ""
                      } ${isEnabled ? "bg-emerald-500" : "bg-gray-300"}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isEnabled ? "translate-x-5" : "translate-x-0"
                        }`}
                    >
                      {isUpdating && (
                        <span className="flex items-center justify-center h-full">
                          <span className="w-2.5 h-2.5 border-2 border-gray-400 border-t-emerald-600 rounded-full animate-spin" />
                        </span>
                      )}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureSettings;
